import type { AppState } from "@soheil/core";
import { applyChanges } from "@soheil/core";
import { seedState, STATE_VERSION } from "./seed";

/**
 * تنها دری که اپ برای خواندن و نوشتن داده دارد.
 * امروز پشتش localStorage است؛ فردا ApiRepo. هیچ کامپوننتی نباید بداند کدام.
 * وقتی بک‌اند آمد، فقط این فایل عوض می‌شود.
 */
export interface Repo {
  load(): Promise<AppState>;
  save(state: AppState): Promise<void | number | undefined>;
  /** اگر لایه سینک دارد: تغییرات سرور را می‌گیرد و ادغام می‌کند */
  pull?(current: AppState): Promise<AppState>;
  /** برای وقتی که سرور آمد: نام کاربرِ متصل، یا undefined در حالت محلی */
  whoami(): Promise<string | undefined>;
}

const KEY = "soheil.state.v1";
const REV_KEY = "soheil.rev.v1";

export class LocalRepo implements Repo {
  async load(): Promise<AppState> {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return seedState();
      const parsed = JSON.parse(raw) as AppState;
      return migrate(parsed);
    } catch {
      // داده خراب بهتر است دور ریخته شود تا اپ بالا نیاید
      return seedState();
    }
  }

  async save(state: AppState): Promise<void> {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      // حالت خصوصی مرورگر یا فضای پر — اپ باید در حافظه کار کند
    }
  }

  async whoami(): Promise<string | undefined> {
    return undefined;
  }
}

/**
 * اسکلت اتصال به بک‌اند. وقتی سرور آماده شد، VITE_API_BASE را پر کنید.
 * initData تلگرام در هدر می‌رود تا سرور با HMAC اعتبارسنجی‌اش کند.
 */
export class ApiRepo implements Repo {
  constructor(private base: string, private initData: string) {}

  private headers(): HeadersInit {
    return {
      "Content-Type": "application/json",
      ...(this.initData ? { "X-Telegram-Init-Data": this.initData } : {}),
    };
  }

  async load(): Promise<AppState> {
    const r = await fetch(`${this.base}/api/state`, { headers: this.headers() });
    if (!r.ok) throw new Error(`load failed: ${r.status}`);
    return migrate((await r.json()) as AppState);
  }

  /** برمی‌گرداند: rev تازهٔ سرور، تا کلاینت بداند تا کجا را دیده. */
  async save(state: AppState): Promise<number | undefined> {
    const r = await fetch(`${this.base}/api/state`, {
      method: "PUT",
      headers: this.headers(),
      body: JSON.stringify(state),
    });
    if (!r.ok) throw new Error(`save failed: ${r.status}`);
    const out = (await r.json()) as { rev?: number };
    return out?.rev;
  }

  /** یک رفت‌وبرگشت سینک: فقط تغییرات بعد از `since`. */
  async sync(since: number): Promise<{ rev: number; changes: Record<string, unknown> } | null> {
    const r = await fetch(`${this.base}/api/sync`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ since }),
    });
    if (r.status === 501) return null; // سرور قدیمی، بدون سینک
    if (!r.ok) throw new Error(`sync failed: ${r.status}`);
    return (await r.json()) as { rev: number; changes: Record<string, unknown> };
  }

  async whoami(): Promise<string | undefined> {
    try {
      const r = await fetch(`${this.base}/api/me`, { headers: this.headers() });
      if (!r.ok) return undefined;
      return ((await r.json()) as { displayName?: string }).displayName;
    } catch {
      return undefined;
    }
  }
}

/**
 * موتور سینک.
 *
 * مسئله: کاربر هم در ربات کار می‌کند هم در مینی‌اپ. تا وقتی هر طرف کل state
 * خودش را می‌نوشت، نوشتنِ دومی، کارِ اولی را بی‌صدا پاک می‌کرد.
 *
 * حالا:
 *   • فرستادن (`push`) با PUT /api/state انجام می‌شود و `__rev` را همراه دارد.
 *     سرور از روی همان می‌فهمد کدام ردیف‌ها را کلاینت دیده بوده، پس هم حذف را
 *     منتقل می‌کند هم نوشتنِ هم‌زمان دستگاه دیگر را زنده نگه می‌دارد.
 *   • گرفتن (`pull`) با POST /api/sync و یک `since` انجام می‌شود؛ فقط چیزی که
 *     عوض شده می‌آید، نه کل داده.
 *   • محلی همیشه نوشته می‌شود. آفلاین بودن یعنی «بعداً می‌فرستیم»، نه «از دست رفت».
 */
export class SyncRepo implements Repo {
  private local = new LocalRepo();
  private rev = 0;
  private pushing: Promise<void> | null = null;
  /** آخرین state ای که اپ داده — برای فرستادنِ دوباره بعد از آفلاین */
  private pending: AppState | null = null;

  constructor(private api: ApiRepo) {
    const saved = Number(localStorage.getItem(REV_KEY));
    if (Number.isFinite(saved)) this.rev = saved;
  }

  async load(): Promise<AppState> {
    const local = await this.local.load();
    try {
      // اولین بار (rev صفر) کل state را می‌گیریم، بعدش فقط تفاوت‌ها
      if (this.rev === 0) {
        const remote = await this.api.load();
        this.setRev(Number((remote as any).__rev) || 0);
        await this.local.save(remote);
        return remote;
      }
      const merged = await this.pull(local);
      return merged;
    } catch {
      return local; // آفلاین — همان محلی
    }
  }

  async save(state: AppState): Promise<void> {
    await this.local.save(state);
    this.pending = state;
    void this.push();
  }

  /** تغییرات سرور را می‌گیرد و در حالت داده‌شده ادغام می‌کند. */
  async pull(current: AppState): Promise<AppState> {
    const out = await this.api.sync(this.rev);
    if (!out) return current;
    this.setRev(out.rev);
    const merged = applyChanges(current, out.changes ?? {});
    if (merged !== current) await this.local.save(merged);
    return merged;
  }

  private async push(): Promise<void> {
    if (this.pushing) return this.pushing; // یک درخواست در پرواز کافی است
    this.pushing = (async () => {
      const state = this.pending;
      this.pending = null;
      if (!state) return;
      try {
        const rev = await this.api.save({ ...state, __rev: this.rev } as AppState);
        if (typeof rev === "number") this.setRev(rev);
      } catch {
        this.pending = state; // آفلاین — دفعهٔ بعد دوباره تلاش می‌شود
      }
    })().finally(() => {
      this.pushing = null;
      if (this.pending) void this.push();
    });
    return this.pushing;
  }

  private setRev(r: number) {
    this.rev = r;
    try { localStorage.setItem(REV_KEY, String(r)); } catch { /* حالت خصوصی */ }
  }

  whoami(): Promise<string | undefined> {
    return this.api.whoami();
  }
}

/** مهاجرت نسخه‌های قدیمی داده. هر نسخهٔ جدید یک بلوک اینجا اضافه می‌کند. */
function migrate(s: AppState): AppState {
  const fresh = seedState();
  const out: AppState = {
    ...fresh,
    ...s,
    version: STATE_VERSION,
    settings: { ...fresh.settings, ...(s.settings ?? {}) },
    progress: { ...fresh.progress, ...(s.progress ?? {}) },
  };
  // آرایه‌های گم‌شده در نسخه‌های قدیمی
  for (const k of ["habits", "habitLogs", "tasks", "mind", "health", "sessions", "stars", "reviews"] as const) {
    if (!Array.isArray(out[k])) (out[k] as unknown) = fresh[k];
  }
  return out;
}

/**
 * آدرس بک‌اند.
 *
 * وقتی مینی‌اپ از خودِ سرور سرو می‌شود (حالت عادی روی تلگرام)، آدرس همان
 * مبدأ صفحه است و لازم نیست جایی نوشته شود. VITE_API_BASE فقط برای وقتی است
 * که اپ و سرور روی دو دامنه باشند — مثلاً هنگام توسعهٔ محلی.
 *
 * قبلاً این مقدار خالی بود و اپ بی‌سروصدا روی localStorage می‌ماند: کاربر در
 * ربات یک چیز می‌دید و در مینی‌اپ چیز دیگر. تشخیصِ خودکار جلوی تکرار آن را می‌گیرد.
 */
function apiBase(initData: string): string | null {
  const explicit = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";
  if (explicit.length > 0) return explicit.replace(/\/$/, "");
  if (typeof location === "undefined") return null;
  if (location.protocol !== "https:") return null; // توسعهٔ محلی
  if (!initData) return null;                      // بیرون از تلگرام، بدون هویت
  return location.origin;
}

export function makeRepo(initData: string): Repo {
  const base = apiBase(initData);
  if (base) return new SyncRepo(new ApiRepo(base, initData));
  return new LocalRepo();
}
